/**
 * StaffAttendanceList — Phase-3b UI for the "Therapist and Other Staff"
 * tab on the existing Attendance page.
 *
 * Two responsibilities:
 *   1. Render one row per staff member (every active user whose role is
 *      NOT Parent or Guardian, plus Employee-Master records that have
 *      no login yet). Data comes from ``GET /api/attendance/staff``.
 *   2. Provide a quick "Present" check-in and an Edit dialog that lets
 *      an admin pick any of the 9 canonical statuses. Editing requires
 *      a mandatory note (backend enforces this too).
 *
 * The 9 statuses live in one place — ``STATUS_META`` — so the badge
 * colours, labels and select options all agree.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtTime } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { CheckCircle, PencilSimple, User, DotsThreeVertical, Warning, X, SignOut, SignIn } from "@phosphor-icons/react";
import { initials, userLabel } from "@/lib/helpers";


// Canonical 9-status list per the Phase-3 spec. `variant` maps to a
// small badge palette so a glance tells you the state.
export const STATUS_META = {
  present:          { label: "Present",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  absent:           { label: "Absent",          cls: "bg-red-100 text-red-800 border-red-200" },
  leave:            { label: "Leave",           cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  late:             { label: "Late Arrival",    cls: "bg-amber-100 text-amber-800 border-amber-200" },
  early_exit:       { label: "Early Exit",      cls: "bg-orange-100 text-orange-800 border-orange-200" },
  half_day_first:   { label: "Half Day — 1st",  cls: "bg-sky-100 text-sky-800 border-sky-200" },
  half_day_second:  { label: "Half Day — 2nd",  cls: "bg-sky-100 text-sky-800 border-sky-200" },
  wfh:              { label: "Work From Home",  cls: "bg-violet-100 text-violet-800 border-violet-200" },
  field:            { label: "Field Visit",     cls: "bg-teal-100 text-teal-800 border-teal-200" },
};

const STATUS_ORDER = [
  "present", "absent", "leave", "late", "early_exit",
  "half_day_first", "half_day_second", "wfh", "field",
];

export function StatusBadge({ status, className = "" }) {
  const meta = STATUS_META[status] || { label: status, cls: "bg-muted text-muted-foreground" };
  return <Badge className={`text-[10px] ${meta.cls} ${className}`} data-testid={`status-badge-${status}`}>{meta.label}</Badge>;
}


export default function StaffAttendanceList({
  roster, attendanceByPerson, branchId, onChanged, search = "", statusFilter = "any",
  canViewContacts = true,
  canOpenEmployee = false,
}) {
  const [editing, setEditing] = useState(null);   // {person, existingRow}
  const navigate = useNavigate();
  const onOpenEmployee = (empId) => {
    if (empId) navigate(`/workforce/employees?highlight=${empId}`);
  };

  const filtered = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    let list = roster;
    if (s) {
      // iter-67 — Consolidated search matches on name, employee_number
      // (via `emp_number`), email (silently even for roles that can't
      // see it in the UI), and designation. Consistent with the
      // AttendancePage top search field.
      list = list.filter(p =>
        (p.name || "").toLowerCase().includes(s)
        || (p.email || "").toLowerCase().includes(s)
        || (p.employee_number || "").toLowerCase().includes(s)
        || (p.emp_number || "").toLowerCase().includes(s)
        || (p.designation || "").toLowerCase().includes(s)
      );
    }
    // iter-55 — status filter shared with the parent AttendancePage.
    if (statusFilter && statusFilter !== "any") {
      list = list.filter((p) => {
        const att = attendanceByPerson[p.id];
        if (!att) return statusFilter === "no_record";
        const status = String(att.status || "").toLowerCase();
        const modifiers = (att.modifiers || []).map((m) => String(m).toLowerCase());
        if (statusFilter === "late") return status === "late" || modifiers.includes("late_arrival");
        if (statusFilter === "early_exit") return status === "early_exit" || modifiers.includes("early_exit");
        if (statusFilter === "leave") return status.endsWith("_leave") || status === "leave";
        return status === statusFilter;
      });
    }
    return list;
  }, [roster, search, statusFilter, attendanceByPerson]);

  if (!roster.length) {
    return <div className="p-6 text-sm text-muted-foreground">No staff found for this branch.</div>;
  }
  if (!filtered.length) {
    return <div className="p-6 text-sm text-muted-foreground">No staff match &ldquo;{search}&rdquo;.</div>;
  }

  const quickPresent = async (person) => {
    if (!branchId || branchId === "all") { toast.error("Pick a branch first"); return; }
    try {
      // iter-66 — Provide a default note so the backend never rejects a
      // fresh Check-In with "note required for editing". The
      // /attendance/staff/mark endpoint treats any pre-existing row for
      // the user on today (including a row from another branch, or a
      // stale off_day / leave row) as an edit and demands a note. For a
      // one-tap Check-In the note "Manual check-in" is a clear enough
      // audit trail — admins who want to add a reason can still open
      // the Edit dialog.
      await api.post("/attendance/staff/mark", {
        user_id: person.id,
        branch_id: branchId,
        date: new Date().toISOString().slice(0, 10),
        status: "present",
        note: "Manual check-in",
      });
      toast.success(`${person.name} checked in`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /**
   * iter-58 — Manual staff check-out. Called from the row-level "Out"
   * button when a staff member has `time_in` but no `time_out`. Mirrors
   * the student flow which POSTs to /attendance/check-out/{attId}. The
   * backend writes `time_out = now` and (for therapist rows) flips
   * therapist_status → off.
   */
  const quickCheckOut = async (person, attId) => {
    try {
      await api.post(`/attendance/check-out/${attId}`);
      toast.success(`${person.name} checked out`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="divide-y divide-border" data-testid="staff-attendance-list">
        {filtered.map(p => {
          const att = attendanceByPerson[p.id];
          const meta = STATUS_META[att?.status];
          const isConflict = att?.conflict_flag;
          return (
            <div
              key={p.id}
              className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3 hover:bg-muted/40"
              data-testid={`staff-row-${p.id}`}
            >
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarFallback className={`text-sm font-semibold ${p.has_app_access === false ? "bg-slate-200 text-slate-700" : "bg-primary/10 text-primary"}`}>
                  {initials(p.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-[160px]">
                <div className="text-sm font-medium truncate flex items-center gap-2 flex-wrap">
                  {/* iter-67 — Clickable name → /workforce/employees?highlight=<id>
                      for owner/admin. Only when we resolved an
                      employee_id (either the row IS an employee master
                      record, or the linked user has a joined employees
                      record). Plain text for everyone else. */}
                  {canOpenEmployee && (p.employee_id || p.has_app_access === false) ? (
                    <button
                      type="button"
                      onClick={() => onOpenEmployee?.(p.employee_id || p.id)}
                      className="truncate text-left hover:text-primary hover:underline underline-offset-2 transition-colors"
                      data-testid={`staff-name-link-${p.id}`}
                    >
                      {p.name}
                    </button>
                  ) : (
                    <span className="truncate">{p.name}</span>
                  )}
                  {p.has_app_access === false && (
                    <Badge variant="outline" className="text-[9px]">No login</Badge>
                  )}
                  {isConflict && (
                    <Badge className="text-[9px] bg-amber-100 text-amber-900 border-amber-200 gap-0.5" data-testid={`conflict-badge-${p.id}`}>
                      <Warning size={9} weight="fill" /> Conflict
                    </Badge>
                  )}
                </div>
                {/* iter-67 — Employee number sub-line. Hidden entirely
                    when the field is missing so no null / undefined
                    leaks. */}
                {p.employee_number && (
                  <div
                    className="font-mono text-[10px] font-semibold tabular-nums text-muted-foreground truncate"
                    data-testid={`employee-id-sub-${p.id}`}
                  >
                    {p.employee_number}
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground truncate">
                  {p.designation || (p.roles || []).join(", ") || "Staff"}
                  {canViewContacts && p.email && ` · ${p.email}`}
                </div>
              </div>

              {att && (
                <StatusBadge status={att.status} />
              )}

              {/* iter-58 — Recorded check-in / check-out times. Matches
                  the student attendance row layout so admins can see the
                  timestamps at a glance without opening the edit dialog.
                  Time_in / time_out are populated by the backend on QR
                  scan, GPS check-in, and Manual mark. */}
              {att && att.time_in && (
                <div className="text-[11px] text-right leading-tight hidden sm:block min-w-[70px]" data-testid={`staff-times-${p.id}`}>
                  <div>In: <span className="text-foreground font-medium">{fmtTime(att.time_in)}</span></div>
                  {att.time_out
                    ? <div>Out: <span className="text-foreground font-medium">{fmtTime(att.time_out)}</span></div>
                    : <div className="text-muted-foreground/70">Out: —</div>}
                </div>
              )}

              {!att ? (
                <Button
                  size="sm"
                  onClick={() => quickPresent(p)}
                  className="gap-1.5"
                  data-testid={`staff-checkin-${p.id}`}
                >
                  <SignIn size={14} weight="bold" /> Check In
                </Button>
              ) : (att.time_in && !att.time_out) ? (
                // iter-58 — Row has an open check-in; give admins a
                // one-click Out button so they can close forgotten
                // check-ins without opening the edit dialog.
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => quickCheckOut(p, att.id)}
                  className="gap-1.5"
                  data-testid={`staff-checkout-${p.id}`}
                >
                  <SignOut size={14} /> Check Out
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing({ person: p, existingRow: att })}
                  className="gap-1.5"
                  data-testid={`staff-edit-${p.id}`}
                >
                  <PencilSimple size={13} /> Edit
                </Button>
              )}

              {/* Overflow menu for less-common actions (kept slim on mobile). */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`staff-more-${p.id}`}>
                    <DotsThreeVertical size={16} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-1" align="end">
                  <button
                    type="button"
                    onClick={() => setEditing({ person: p, existingRow: att || null })}
                    className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-muted"
                    data-testid={`staff-set-status-${p.id}`}
                  >
                    {att ? "Change status" : "Set status…"}
                  </button>
                </PopoverContent>
              </Popover>
            </div>
          );
        })}
      </div>

      {editing && (
        <StaffAttendanceEditDialog
          open={!!editing}
          person={editing.person}
          existingRow={editing.existingRow}
          branchId={branchId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged?.(); }}
        />
      )}
    </>
  );
}


function StaffAttendanceEditDialog({ open, person, existingRow, branchId, onClose, onSaved }) {
  const isEdit = !!existingRow;
  const [status, setStatus] = useState(existingRow?.status || "present");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const noteRequired = isEdit;
  const canSave = status && (!noteRequired || note.trim());

  const save = async () => {
    if (!branchId || branchId === "all") { toast.error("Pick a branch first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/attendance/staff/mark", {
        user_id: person.id,
        branch_id: branchId,
        date: existingRow?.date || new Date().toISOString().slice(0, 10),
        status,
        note: note || undefined,
      });
      // Post-payroll gate: server didn't apply the edit, it queued a
      // correction request for a payroll manager to review.
      if (data?.status === "pending_review" && data?.pending_correction_id) {
        toast.info(
          "Sent for payroll-manager review — this date is inside a locked payroll run.",
          { duration: 5000 },
        );
      } else {
        toast.success(`${person.name} — ${STATUS_META[status]?.label || status}`);
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="staff-att-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User size={16} weight="duotone" className="text-primary" /> {isEdit ? "Edit attendance" : "Mark attendance"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {person?.name}
            {existingRow?.date && ` · ${existingRow.date}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {existingRow?.is_post_payroll && (
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-[11px] px-3 py-2 flex items-start gap-2" data-testid="post-payroll-warning">
              <Warning size={12} className="mt-0.5" />
              <span>This date falls in a payroll period that has already been generated. A Post-Payroll Correction will be raised for the payroll manager to review.</span>
            </div>
          )}
          {existingRow?.conflict_flag && (
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-[11px] px-3 py-2 flex items-start gap-2">
              <Warning size={12} className="mt-0.5" />
              <span>This row conflicts with an approved leave. Choose whether to keep the manual entry (this edit clears the flag) or cancel the leave.</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="staff-att-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map(s => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Note {noteRequired && <span className="text-red-600">*</span>}</Label>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={noteRequired ? "Required — reason for change" : "Optional"}
              data-testid="staff-att-note"
            />
            {noteRequired && (
              <p className="text-[10px] text-muted-foreground">A note is mandatory when editing existing attendance.</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="gap-1" data-testid="staff-att-cancel">
            <X size={13} /> Cancel
          </Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="staff-att-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
